<template>
  <div class="info-menu">
    <button
      type="button"
      class="info-button"
      :class="{ new: secureInfoList.length > 0, 'icon-button__selected': isInfoListVisible }"
      @click="showSecureList"
    >
      <i class="fal fa-exclamation-circle"></i>
    </button>

    <div v-show="isInfoListVisible" class="alarm-detail" ref="infoDetail" tabindex="0" @focusout="closeDropdownMenu">
      <div class="alarm-detail-header">
        <h3>{{ $t('보안 이벤트') }}</h3>
        <a :href="getHelpUrl('TURN_OFF')" target="_self" class="officehome-gnb-security">{{ $t('알림 끄기') }}</a>
      </div>

      <div class="alarm-detail-count">
        <template v-if="secureInfoList.length">
          <div v-html="$t('PLATFORM-SECURITY-CNT', { cnt: secureInfoList.length })"></div>
        </template>
        <template v-else>
          <div class="no-alarm">
            {{ $t('알림이 없습니다') }}
          </div>
        </template>
      </div>

      <div v-if="secureInfoList.length" class="alarm-list">
        <a
          v-for="(secureInfo, idx) in secureInfoList"
          :key="`ifa-${idx}`"
          :href="getHelpUrl(secureInfo.alarm_type)"
          target="_self"
          class="alarm-list-item"
        >
          <i class="fal fa-exclamation-circle"></i>
          <div v-html="getAlarmTextHtml(secureInfo)"></div>
        </a>
      </div>
      <div v-if="secureInfoList.length" class="alarm-footer">
        <a :href="getHelpUrl('MAIN')" target="_self" class="officehome-gnb-security">
          {{ $t('조치하기') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlatformInfoCenter',
  props: {
    secureInfoList: {
      type: Array,
      required: true,
    },
    isInfoListVisible: {
      type: Boolean,
      required: true,
    },
    domain: {
      type: String,
      required: true,
    },
  },
  methods: {
    showSecureList() {
      this.$emit('set-is-info-visible', !this.isInfoListVisible);
      !this.isInfoListVisible && this.$nextTick(() => this.$refs.infoDetail.focus());
    },
    closeDropdownMenu(e) {
      const isMenuButton = Boolean(e.relatedTarget?.closest('div.info-menu'));
      if (!isMenuButton) this.$emit('set-is-info-visible', false);
    },
    getHelpUrl(alarm_type) {
      if (!this.domain) {
        return '#';
      }

      const officeMainServiceUrl = `${process.env.VUE_APP_OFFICE_URL}/${this.domain}`;

      if (alarm_type === 'TURN_OFF') {
        return `${officeMainServiceUrl}/mypage/manager/security_alert`;
      }

      return `${officeMainServiceUrl}/mypage/manager/security_center`;
    },
    getAlarmTextHtml(data) {
      if (data.alarm_type === 'PASSWORD_EXPIRED') {
        return `<p class="desc-txt">${this.$t('비밀번호 변경 후 6개월 이상 경과되었습니다.')}</p>`;
      }

      if (data.alarm_type === 'OTP') {
        return `<p class="desc-txt">${this.$t('2단계 인증 설정을 사용하고 있지 않습니다.')}</p><p>${this.$t(
          '설정의 보안 취약점에 대해 알아보세요.',
        )}</p>`;
      }

      if (data.alarm_type === 'POP3_SMTP') {
        return `<p class="desc-txt">${this.$t('POP3/SMTP 설정을 사용중입니다.')}</p><p>${this.$t(
          '설정의 보안 취약점에 대해 알아보세요.',
        )}</p>`;
      }

      if (data.alarm_type === 'FOREIGN_LOGIN') {
        const getLoginDateLocaleStr = (language, dateStr) => {
          if (language === 'korean') {
            const dateArr = dateStr.split('-');
            return `${dateArr[0]}년 ${dateArr[1]}월 ${dateArr[2]}일`;
          }
          return dateStr;
        };

        return `<p class="desc-txt">${this.$t('FOREIGN_LOGIN_STATE', {
          date: getLoginDateLocaleStr(this.$i18n.locale, data.content.login_date.split('T')[0]),
        })}</p>`;
      }

      if (data.alarm_type === 'MAIL_BLOCK') {
        if (data.content.block_type === 'PHISHING') {
          return `<p class="desc-txt">${this.$t('비정상적인 메일 발송이 감지되었습니다.')}</p><p>${this.$t(
            '메일 발송이 차단되었습니다.',
          )}</p>`;
        }
        if (data.content.block_type === 'SUSPEND') {
          return `<p class="desc-txt">${this.$t('메일 발송 정책에 위배된 사용이 확인되었습니다.')}</p><p>${this.$t(
            '24시간 동안 메일 발송이 정지되었습니다.',
          )}</p>`;
        }
      }

      if (data.alarm_type === 'NEW_COUNTRY_LOGIN') {
        return `<p class="desc-txt">${this.$t('새로운 국가에서 로그인되었습니다.')}</p>`;
      }

      if (data.alarm_type === 'NEW_IP_LOGIN') {
        return `<p class="desc-txt">${this.$t('새로운 IP에서 로그인되었습니다.')}</p>`;
      }

      if (data.alarm_type === 'MAIL_PASSWORD_CREATED') {
        return `<p class="desc-txt">${this.$t('메일 전용 비밀번호가 설정되었습니다.')}</p>`;
      }

      if (data.alarm_type === 'MAIL_PASSWORD_REISSUED') {
        return `<p class="desc-txt">${this.$t('메일 전용 비밀번호가 갱신되었습니다.')}</p>`;
      }

      if (data.alarm_type === 'MAIL_PASSWORD_DELETED') {
        return `<p class="desc-txt">${this.$t('메일 전용 비밀번호가 삭제되었습니다.')}</p>`;
      }

      if (data.alarm_type === 'POP3_SMTP_WITHOUT_MAIL_PASSWORD') {
        return `<p class="desc-txt">${this.$t(
          '메일 전용 비밀번호를 설정하지 않고 POP3/SMTP 설정을 사용중입니다.',
        )}</p>`;
      }

      return data.alarm_type;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/base/variables.scss';
@import '@/assets/scss/mixins/button.scss';
@import '@/assets/scss/mixins/icon.scss';

.info-menu {
  .info-button {
    width: 34px;
    height: 34px;
    font-size: 20px;
  }

  @media (min-width: 900px) {
    position: relative;
  }

  .alarm-detail {
    position: absolute;
    width: 320px;
    top: $managementHeaderHeight;
    padding: 16px;
    background: #fff;
    border: solid 1px #909090;
    border-radius: 4px;
    box-shadow: 2px 5px 4px 0 rgba(0, 0, 0, 0.16);
    max-height: calc(100vh - 70px);
    overflow: auto;
    right: 20px;

    .alarm-detail-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 22px;

      h3 {
        color: #333;
        font-weight: bold;
        align-items: flex-start;
        font-size: 13px;
      }

      a {
        height: 26px;
        padding: 0px 10px;
        border: 1px solid #d6d6d6;
        line-height: 22px;
        border-radius: 4px;
        background-color: #fff;
        color: #333;
        font-size: 13px;

        &:hover {
          background-color: #eaeff2;
        }

        &:active {
          color: rgba(51, 51, 51, 0.5);
        }
      }
    }

    .alarm-detail-count {
      font-size: 12px;
      margin-bottom: 8px;
      color: #333;

      .no-alarm {
        color: #c4c4c4;
      }
    }

    .alarm-header {
      height: 61px;
      margin: 0 20px;
      border-bottom: 1px solid #d6d6d6;
      font-weight: 400;
      font-size: 16px;
    }

    .no-alarm {
      text-align: center;
      padding-top: 30px;
      padding-bottom: 40px;
      color: #c4c4c4;
    }

    .alarm-list {
      height: 100%;
      font-size: 13px;
      color: #333;
      padding: 0;

      .alarm-list-item {
        display: flex;
        padding-top: 16px;
        padding-bottom: 16px;
        position: relative;
        letter-spacing: -0.21px;
        color: #333;
        box-sizing: border-box;

        i {
          font-size: 16px;
          margin-top: 2px;
          margin-right: 6px;
          color: #d84a49;
        }

        &::before {
          content: '';
          position: absolute;
          display: block;
          width: 100%;
          top: 0;
          height: 1px;
          background-color: #eaeaea;
        }
      }
    }

    .alarm-footer {
      display: flex;
      justify-content: center;
      align-items: center;

      a {
        height: 26px;
        padding: 0 10px;
        margin-top: 16px;
        line-height: 22px;
        border: 1px solid transparent;
        border-radius: 4px;
        background-color: #d84a49;
        color: #fff;
        font-size: 13px;

        &:hover {
          background-color: #c70909;
        }

        &:active {
          color: rgba(255, 255, 255, 0.5);
        }
      }
    }

    @media (min-width: 900px) {
      position: absolute;
      top: 42px;
      right: 0;
    }
  }
}

.info-button {
  width: $iconButtonSize;
  height: $iconButtonSize;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  color: #d84a49;
  border-radius: 4px;
  &:active {
    color: #c70909;
  }
}

.info-button {
  &:hover {
    background-color: rgba(159, 166, 173, 0.2);
  }
}

.info-main-title {
  margin-top: 2px;
  color: #333;
}

.info-icon-mini {
  @include main-menu-icon('small-info-danger');
  height: 18px;
  width: 18px;
  margin-right: 12px;
}

.only-right-item {
  margin-left: auto;
}
</style>
